/**
 * Documentation Page
 * Dynamic documentation browser with sidebar navigation
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface DocSection {
  id: string;
  title: string;
  icon?: string;
  file?: string;
  description?: string;
  children?: DocSection[];
}

interface DocsIndex {
  title: string;
  version: string;
  lastUpdate: string;
  sections: DocSection[];
}

export default function DocumentationPage() {
  const [index, setIndex] = useState<DocsIndex | null>(null);
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [activeDoc, setActiveDoc] = useState<string>('overview');
  const [expandedSections, setExpandedSections] = useState<string[]>(['business']);

  useEffect(() => {
    loadIndex();
  }, []);

  useEffect(() => {
    if (index) {
      loadDocument(activeDoc);
    }
  }, [activeDoc, index]);

  const loadIndex = async () => {
    setLoading(true);
    try {
      const response = await fetch('/docs/index.json');
      const data = await response.json();
      setIndex(data);
    } catch (error) {
      console.error('Failed to load docs index:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDocument = async (docId: string) => {
    setLoadingDoc(true);
    try {
      // Find document in index
      let file: string | undefined;

      // Search in sections
      for (const section of index?.sections || []) {
        if (section.file && section.id === docId) {
          file = section.file;
          break;
        }
        if (section.children) {
          const child = section.children.find(c => c.id === docId);
          if (child?.file) {
            file = child.file;
            break;
          }
        }
      }

      if (file) {
        const response = await fetch(`/docs/${file}`);
        const text = await response.text();
        setContent(text);
      } else {
        setContent('# Documento non trovato\n\nIl documento richiesto non esiste.');
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      setContent('# Errore\n\nImpossibile caricare il documento.');
    } finally {
      setLoadingDoc(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento Documentazione...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <svg
                  className="w-7 h-7 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">📚 {index?.title}</h1>
                <p className="text-gray-600 text-sm mt-1">
                  v{index?.version} • Aggiornato: {index?.lastUpdate}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
              <nav className="space-y-1">
                {/* Sections */}
                {index?.sections.map((section) => {
                  const isExpanded = expandedSections.includes(section.id);
                  const hasChildren = section.children && section.children.length > 0;

                  return (
                    <div key={section.id} className="space-y-1">
                      {/* Section Header */}
                      {hasChildren ? (
                        <button
                          onClick={() => toggleSection(section.id)}
                          className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div className="flex items-center gap-2">
                            <span>{section.icon}</span>
                            <span>{section.title}</span>
                          </div>
                          <svg
                            className={`w-4 h-4 transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      ) : (
                        <button
                          onClick={() => section.file && setActiveDoc(section.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                            activeDoc === section.id
                              ? 'bg-indigo-600 text-white font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <span>{section.icon}</span>
                          <span>{section.title}</span>
                        </button>
                      )}

                      {/* Children */}
                      {hasChildren && isExpanded && (
                        <div className="ml-4 space-y-1 pl-3 border-l-2 border-gray-200">
                          {section.children?.map((child) => (
                            <button
                              key={child.id}
                              onClick={() => setActiveDoc(child.id)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                activeDoc === child.id
                                  ? 'bg-indigo-600 text-white font-medium'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              {child.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </motion.aside>

          {/* Main Content */}
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-3"
          >
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              {loadingDoc ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Caricamento...</p>
                </div>
              ) : (
                <div className="prose prose-slate max-w-none">
                  <ReactMarkdown
                    rehypePlugins={[rehypeRaw]}
                    components={{
                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <SyntaxHighlighter
                            style={vscDarkPlus}
                            language={match[1]}
                            PreTag="div"
                            {...props}
                          >
                            {String(children).replace(/\n$/, '')}
                          </SyntaxHighlighter>
                        ) : (
                          <code
                            className="bg-gray-100 text-pink-600 px-1.5 py-0.5 rounded text-sm font-mono"
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      },
                      h1: ({ children }) => (
                        <h1 className="text-3xl font-bold text-gray-900 mb-4 mt-0">
                          {children}
                        </h1>
                      ),
                      h2: ({ children }) => (
                        <h2 className="text-2xl font-semibold text-gray-900 mb-3 mt-8">
                          {children}
                        </h2>
                      ),
                      h3: ({ children }) => (
                        <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-6">
                          {children}
                        </h3>
                      ),
                      a: ({ children, href, ...props }) => (
                        <a
                          className="text-indigo-600 hover:text-indigo-800 underline"
                          href={href}
                          {...props}
                        >
                          {children}
                        </a>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto my-4">
                          <table className="min-w-full divide-y divide-gray-200">
                            {children}
                          </table>
                        </div>
                      ),
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-4 border-indigo-500 pl-4 italic text-gray-700 my-4">
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
              )}
            </div>
          </motion.main>
        </div>
      </div>
    </div>
  );
}
